/* Margin Finder · Perfil: quem é a pessoa, em texto corrido. E só isso.
 *
 * A PRIMEIRA sessão da aba, e a única que não fala de dinheiro a economizar. As outras
 * respondem "onde há margem"; esta responde "de quem estamos falando" — é o retrato que
 * dá sentido às demais, e por isso abre a aba.
 *
 * Nada de herói de economia, cartões ou tabelas. O `agent_profile` publica um markdown
 * de ~4 KB com nove seções de prosa, e a leitura certa dele é ler: `<Markdown>` no
 * `.prose` de 70ch, aberto, sem <details> na frente. Ele TAMBÉM publica um
 * `user_analysis` grande, que esta tela ignora de propósito — um perfil desmontado em
 * grade de fatos diz menos que o parágrafo que o agente escreveu.
 *
 * Por ler `/api/reports/latest` — a mesma chave que a aba Relatórios e o
 * `<RelatorioEmTexto>` das outras sessões já usam — esta sessão não custa nenhuma
 * requisição nova, e o JSON do perfil nunca chega ao navegador. */

import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { ReportsLatestPayload } from "../../lib/api";
import { compact, fmtDateTime, shortId } from "../../lib/format";
import { Card, Empty, ErrorState, Markdown, Skeleton } from "../../components/ui";
import { AgentsRefresh } from "../../components/AgentsRefresh";
import { useUsers } from "../../components/Shell";

const AGENT_ID = "agent_profile";

export function Perfil() {
  const [params, setParams] = useSearchParams();
  const { data: painelUsers } = useUsers();

  const q = useQuery({
    // Mesmas opções da aba Relatórios, obrigatoriamente: `staleTime` é por observador,
    // e um valor menor aqui faria ESTA montagem revalidar a chave inteira.
    queryKey: ["reports-latest"],
    queryFn: () => api<ReportsLatestPayload>("/api/reports/latest"),
    staleTime: Infinity,
    gcTime: 12 * 60 * 60_000,
    retry: false,
    placeholderData: (previous) => previous,
  });
  const data = q.data;

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  // Só quem TEM perfil entra no <select>: oferecer um usuário e mostrar vazio é pior
  // que não oferecer. `users` já vem ordenado do servidor.
  const comPerfil = (data?.users ?? []).filter((u) => u.agents[AGENT_ID]);
  const escolhido = params.get("usuario");
  const padrao = comPerfil[0]?.user_id;
  const userId = escolhido || padrao || "";
  const atual = comPerfil.find((u) => u.user_id === userId);
  const report = atual?.agents[AGENT_ID] ?? null;
  // O `?usuario=` atravessa as chips do Margin Finder; nem todo usuário das outras
  // sessões tem perfil escrito (assinaturas, por exemplo, tem 50 contra 35 aqui).
  const forasteiro = Boolean(userId && !atual);

  /* Sendo a PRIMEIRA sessão, é normalmente esta que decide o usuário padrão de toda a
   * aba: ela fixa o escolhido na URL e as outras chips herdam. Ver `SecaoMargin`, que
   * faz o mesmo para quem entrar direto por outra sessão. */
  useEffect(() => {
    if (escolhido || !padrao) return;
    const next = new URLSearchParams(params);
    next.set("usuario", padrao);
    setParams(next, { replace: true });
  }, [escolhido, padrao]);   // eslint-disable-line react-hooks/exhaustive-deps

  const label = (uid: string) => {
    const conhecido = painelUsers?.find((p) => !p.is_aggregate && p.user_id === uid);
    return conhecido
      ? `${shortId(uid)}…  ·  ${conhecido.months} meses  ·  R$ ${compact(conhecido.total)}`
      : `${shortId(uid)}…`;
  };

  if (q.isPending) return <Skeleton rows={10} />;
  // Só troca a tela pelo erro quando não há dado nenhum: um soluço no refetch do
  // "Atualizar dados" não pode apagar o perfil que já estava lido.
  if (q.error && !data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (!data) return null;

  return (
    <>
      {q.error && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
      <div className="controls">
        {comPerfil.length > 0 && (
          <label>usuário
            <select value={userId} onChange={(e) => set("usuario", e.target.value)}>
              {forasteiro && (
                <option value={userId} disabled>
                  {shortId(userId)}…  ·  sem perfil escrito
                </option>
              )}
              {comPerfil.map((u) => (
                <option key={u.user_id} value={u.user_id}>{label(u.user_id)}</option>
              ))}
            </select>
          </label>
        )}
        <AgentsRefresh fetchedAt={data.fetched_at} stale={data.stale} />
      </div>

      {!report
        ? <Empty onClear={() => set("usuario", "")}>
            {forasteiro
              ? <>Nenhum perfil escrito para <code>{shortId(userId)}…</code>, o usuário
                escolhido nas outras abas. Escolha outro acima, ou limpe para ver o
                primeiro com perfil.</>
              : "Nenhum perfil escrito para este usuário."}
          </Empty>
        : (
          /* O cartao acompanha a medida de leitura do `.prose` (70ch) em vez de
             esticar ate a borda: texto corrido colado na esquerda de um cartao de
             1400px parece pagina quebrada, nao coluna de leitura. */
          <div className="mf-page" style={{ maxWidth: "80ch" }}>
            <Card>
              <Markdown text={report.user_report} />
            </Card>
            <div className="mf-provenance">
              gerado em {fmtDateTime(report.created_at)} · <code>{AGENT_ID}</code>
              {" · "}
              <Link to={`/relatorios?usuario=${userId}&modo=um&agente=${AGENT_ID}`}>
                abrir em Relatórios
              </Link>
            </div>
          </div>
        )}
    </>
  );
}
